/**
 * Aviso "mandar o próximo paciente entrar" — médico → recepção, em tempo real
 * (RTDB).
 *
 * É o ESPELHO INVERSO do [[doctorAlert]]/[[NextPatientWatcher]] (recepção →
 * médico): aqui o MÉDICO, pelo topo da agenda lateral ([[DayAgendaDrawer]]),
 * dispara um aviso GENÉRICO pedindo que a recepção mande o próximo paciente
 * entrar — SEM dizer quem é o próximo (a recepção decide pela ordem da fila). O
 * aviso aparece como POP-UP na tela da recepção ([[CallNextWatcher]]), em
 * qualquer página do sistema — o watcher é global no Shell.
 *
 * Efêmero por natureza: vive em `receptionAlerts/{clinicId}/{pushId}` no Realtime
 * Database e some quando a recepção dá "Ciente" (ou quando envelhece). O nó é
 * por clínica; quem pediu (`sentByUid` = uid do médico = professionalId) decide
 * qual recepção vê: o [[CallNextWatcher]] respeita o escopo de agenda do
 * perfil (`allowedProfessionalIds`, vazio = todos os médicos) e o local de
 * atendimento (`serviceLocal`, deduzido de onde os pacientes do médico
 * chegaram hoje — ele circula entre lugares com recepções diferentes).
 */
import { rtdb } from './config';
import { QueueService } from './queue';
import {
  ref, push, set, onValue, off, remove, serverTimestamp,
  query, orderByChild, limitToLast, DataSnapshot,
} from 'firebase/database';

export interface ReceptionAlertData {
  id: string;
  /**
   * Sala/consultório do médico, se informada — ajuda a recepção a saber pra
   * onde mandar o paciente. Opcional; o aviso continua genérico quanto a QUEM
   * é o próximo.
   */
  room?: string;
  /** Onde o médico está atendendo (ver utils/serviceLocal). Ausente = não
   *  deu pra saber → todas as recepções do escopo veem. */
  serviceLocal?: string;
  /** Quem pediu (médico). */
  sentByUid: string;
  sentByName?: string;
  /** serverTimestamp resolvido (ms). */
  createdAt: number;
}

/** Evento de janela disparado a cada "mandar entrar" enviado com sucesso. */
export const CALL_NEXT_SENT_EVENT = 'dratento:call-next-sent';
export interface CallNextSentDetail {
  clinicId: string;
  alertId: string;
}

function alertsRef(clinicId: string) {
  return ref(rtdb, `receptionAlerts/${clinicId}`);
}

export const ReceptionAlertService = {
  /** Dispara um aviso genérico de "mandar o próximo entrar" pra tela da recepção. */
  async sendCallNext(
    clinicId: string,
    payload: {
      room?: string;
      sentByUid: string;
      sentByName?: string;
    },
  ): Promise<string> {
    let serviceLocal: string | undefined;
    try {
      serviceLocal = await QueueService.resolveLocal(clinicId, payload.sentByUid);
    } catch (err) {
      console.warn('[reception-alert] local de atendimento não resolveu:', err);
    }
    const node = push(alertsRef(clinicId));
    // RTDB apaga campos setados como null — vira "ausente", que é o que
    // queremos pra sala/nome sem valor.
    await set(node, {
      room: payload.room ?? null,
      serviceLocal: serviceLocal ?? null,
      sentByUid: payload.sentByUid,
      sentByName: payload.sentByName ?? null,
      createdAt: serverTimestamp(),
    });
    // Quem pediu vê a confirmação ([[CallNextConfirm]]) venha do botão ou do
    // "Atlas, manda entrar" — todo envio passa por aqui.
    window.dispatchEvent(
      new CustomEvent<CallNextSentDetail>(CALL_NEXT_SENT_EVENT, {
        detail: { clinicId, alertId: node.key! },
      }),
    );
    return node.key!;
  },

  /**
   * Acompanha UM aviso (lado do médico): `cb(true)` enquanto existe, `cb(false)`
   * quando some — a recepção deu "Ciente" (a limpeza por idade só vem depois
   * de 5 min). Retorna o unsubscribe.
   */
  watch(clinicId: string, alertId: string, cb: (exists: boolean) => void): () => void {
    const r = ref(rtdb, `receptionAlerts/${clinicId}/${alertId}`);
    const handler = (snap: DataSnapshot) => cb(snap.exists());
    onValue(r, handler);
    return () => off(r, 'value', handler);
  },

  /** Assina os avisos da clínica (lado da recepção). Retorna o unsubscribe. */
  subscribe(
    clinicId: string,
    cb: (alerts: ReceptionAlertData[]) => void,
  ): () => void {
    const q = query(alertsRef(clinicId), orderByChild('createdAt'), limitToLast(10));
    const handler = (snap: DataSnapshot) => {
      const out: ReceptionAlertData[] = [];
      snap.forEach((child) => {
        const v = (child.val() ?? {}) as Partial<ReceptionAlertData>;
        out.push({
          id: child.key!,
          room: v.room ?? undefined,
          serviceLocal: v.serviceLocal ?? undefined,
          sentByUid: v.sentByUid ?? '',
          sentByName: v.sentByName ?? undefined,
          createdAt: typeof v.createdAt === 'number' ? v.createdAt : 0,
        });
      });
      cb(out);
    };
    onValue(q, handler);
    return () => off(q, 'value', handler);
  },

  /** Remove um aviso já visto/confirmado (ou envelhecido). */
  async dismiss(clinicId: string, alertId: string): Promise<void> {
    await remove(ref(rtdb, `receptionAlerts/${clinicId}/${alertId}`));
  },
};
